"use client";

import { useEffect } from "react";
import { getCurrencyFromCountry, SUPPORTED_CURRENCIES } from "@cede/types";
import { useCurrencyRates } from "./useCurrencyRates";
import { usePolymarketGeoblock } from "@/hooks/polymarket/usePolymarketGeoblock";
import { useCurrencyStore, useSelectedCurrencyMetadata } from "@/store/currencyStore";

/**
 * Hook to manage user's currency preference with auto-detection
 *
 * On first load, it will:
 * 1. Fetch the user's country from Polymarket geoblock API
 * 2. Auto-select their local currency if supported
 * 3. Persist the selection to localStorage
 *
 * On subsequent loads, it will use the persisted selection.
 */
export const useUserCurrency = () => {
  const { rates, isLoading: isLoadingRates } = useCurrencyRates();

  const { selectedCurrency, setSelectedCurrency, hasAutoDetected, setHasAutoDetected, convertFromUsdt, getRate } =
    useCurrencyStore();

  const currencyMetadata = useSelectedCurrencyMetadata();

  // Get user's country for auto-detection
  const { data: geolocationData, isLoading: isLoadingGeo } = usePolymarketGeoblock();

  // Auto-detect currency on first load
  useEffect(() => {
    // Skip if already auto-detected or still loading
    if (hasAutoDetected || isLoadingGeo) {
      return;
    }

    // If we have geolocation data, set the currency
    if (geolocationData?.country) {
      const countryCode = geolocationData.country.toUpperCase();
      const detectedCurrency = getCurrencyFromCountry(countryCode);

      // Only set if it's different from current and is supported
      if (detectedCurrency && SUPPORTED_CURRENCIES[detectedCurrency]) {
        setSelectedCurrency(detectedCurrency);
      }

      // Mark as auto-detected regardless of result
      setHasAutoDetected(true);
    }
  }, [geolocationData, hasAutoDetected, isLoadingGeo, setSelectedCurrency, setHasAutoDetected]);

  return {
    /** Current selected currency code (lowercase) */
    selectedCurrency,
    /** Set the selected currency */
    setSelectedCurrency,
    /** Current currency metadata (symbol, name, flag, etc.) */
    currencyMetadata,
    /** Conversion rates from USDT */
    rates,
    /** Whether rates are loading */
    isLoadingRates,
    /** Get the current conversion rate */
    getRate,
    /** Convert a USDT value to the selected currency */
    convertFromUsdt,
    /** Whether we're loading geolocation */
    isLoadingGeo,
    /** User's detected country code */
    detectedCountry: geolocationData?.country ?? null,
    /** Check if using USD (no conversion display needed) */
    isUsd: selectedCurrency === "usd",
  };
};

/**
 * Get list of all supported currencies for the selector
 */
export const useSupportedCurrencies = () => {
  return Object.values(SUPPORTED_CURRENCIES).sort((a, b) => {
    // Put USD first, then sort alphabetically by name
    if (a.code === "usd") return -1;
    if (b.code === "usd") return 1;
    return a.name.localeCompare(b.name);
  });
};
